import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useMusicSettings } from "@/hooks/useMusicSettings";
import { useSoundtracks, SOUNDTRACKS } from "@/hooks/useSoundtracks";
import { useAuth } from "@/hooks/useAuth";
import { Settings, Music, Volume2, VolumeX, Lock, Eye, EyeOff, Check, X, Loader2, Disc3, LockKeyhole } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SettingsModal({ isOpen, onClose }) {
  const {
    musicEnabled,
    soundEffectsEnabled,
    volume,
    currentSoundtrackId,
    toggleMusic,
    toggleSoundEffects,
    setMusicVolume,
    setSoundtrack,
  } = useMusicSettings();
  
  const { isAuthenticated } = useAuth();
  
  // Local soundtrack state - fetched directly when modal opens
  const [localUnlockedSoundtracks, setLocalUnlockedSoundtracks] = useState(['woody']);
  const [soundtracksLoading, setSoundtracksLoading] = useState(false);
  
  // Soundtrack selection state
  const [showSoundtrackSection, setShowSoundtrackSection] = useState(false);
  const [selectingSoundtrack, setSelectingSoundtrack] = useState(null);
  
  // Fetch soundtracks directly when modal opens
  useEffect(() => {
    const fetchSoundtracksLocal = async () => {
      const token = localStorage.getItem('fruitCrush_token');
      console.log('[SettingsModal] Effect running, isOpen:', isOpen, 'token:', !!token);
      if (!isOpen || !token) return;
      
      setSoundtracksLoading(true);
      try {
        // Sync first
        await fetch(`${API_URL}/api/soundtracks/sync`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Then get progress
        const response = await fetch(`${API_URL}/api/progress`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          const unlocked = data.unlocked_soundtracks || ['woody'];
          console.log('[SettingsModal] Loaded soundtracks:', unlocked);
          setLocalUnlockedSoundtracks(unlocked);
        }
      } catch (error) {
        console.error('Failed to fetch soundtracks:', error);
      } finally {
        setSoundtracksLoading(false);
      }
    };
    
    fetchSoundtracksLocal();
  }, [isOpen, isAuthenticated]);
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const resetPasswordFields = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowPasswordSection(false);
  };

  // Handle soundtrack selection
  const handleSelectSoundtrack = async (soundtrackId) => {
    if (!localUnlockedSoundtracks.includes(soundtrackId)) {
      toast.error("You haven't unlocked this soundtrack yet!");
      return;
    }
    
    const token = localStorage.getItem('fruitCrush_token');
    setSelectingSoundtrack(soundtrackId);
    try {
      const response = await fetch(`${API_URL}/api/soundtracks/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ soundtrack_id: soundtrackId })
      });
      
      if (response.ok) {
        setSoundtrack(soundtrackId);
        toast.success(`Now playing: ${SOUNDTRACKS[soundtrackId]?.name || 'Soundtrack'}`);
      } else {
        toast.error("Failed to select soundtrack");
      }
    } catch (error) {
      toast.error("Failed to select soundtrack");
    } finally {
      setSelectingSoundtrack(null);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (!newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Something went wrong");
      }

      toast.success("Password changed successfully! 🎉");
      resetPasswordFields();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleClose = () => {
    resetPasswordFields();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl text-amber-800">
            <Settings className="w-6 h-6" />
            Settings
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Background Music Toggle */}
          <div className="flex items-center justify-between p-4 bg-white/60 rounded-xl border border-amber-200">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${musicEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                {musicEnabled ? (
                  <Music className="w-5 h-5 text-green-600" />
                ) : (
                  <VolumeX className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div>
                <Label htmlFor="music-toggle" className="font-display font-semibold text-amber-900">
                  Background Music
                </Label>
                <p className="text-xs text-amber-600">Cheerful melody while playing</p>
              </div>
            </div>
            <Switch
              id="music-toggle"
              checked={musicEnabled}
              onCheckedChange={toggleMusic}
              data-testid="music-toggle-switch"
            />
          </div>

          {/* Music Volume Slider */}
          {musicEnabled && (
            <div className="p-4 bg-white/60 rounded-xl border border-amber-200 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-display font-semibold text-amber-900">
                  Music Volume
                </Label>
                <span className="text-sm text-amber-600 font-mono">
                  {Math.round(volume * 100)}%
                </span>
              </div>
              <Slider
                value={[volume * 100]}
                onValueChange={(value) => setMusicVolume(value[0] / 100)}
                max={100}
                min={0}
                step={5}
                className="w-full"
                data-testid="volume-slider"
              />
              <div className="flex justify-between text-xs text-amber-500">
                <span>🔈 Quiet</span>
                <span>🔊 Loud</span>
              </div>
            </div>
          )}

          {/* Sound Effects Toggle */}
          <div className="flex items-center justify-between p-4 bg-white/60 rounded-xl border border-amber-200">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${soundEffectsEnabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                {soundEffectsEnabled ? (
                  <Volume2 className="w-5 h-5 text-blue-600" />
                ) : (
                  <VolumeX className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div>
                <Label htmlFor="sfx-toggle" className="font-display font-semibold text-amber-900">
                  Sound Effects
                </Label>
                <p className="text-xs text-amber-600">Click and match sounds</p>
              </div>
            </div>
            <Switch
              id="sfx-toggle"
              checked={soundEffectsEnabled}
              onCheckedChange={toggleSoundEffects}
              data-testid="sfx-toggle-switch"
            />
          </div>

          {/* Soundtrack Selection Section */}
          <div className="p-4 bg-white/60 rounded-xl border border-amber-200">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowSoundtrackSection(!showSoundtrackSection)}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-pink-100">
                  <Disc3 className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <Label className="font-display font-semibold text-amber-900 cursor-pointer">
                    Soundtrack
                  </Label>
                  <p className="text-xs text-amber-600">
                    {SOUNDTRACKS[currentSoundtrackId]?.emoji} {SOUNDTRACKS[currentSoundtrackId]?.name || 'Woody'}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-amber-700"
                data-testid="toggle-soundtrack-section"
              >
                {showSoundtrackSection ? "Hide" : "Change"}
              </Button>
            </div>

            {/* Soundtrack List */}
            {showSoundtrackSection && (
              <div className="mt-4 space-y-2 pt-4 border-t border-amber-200 max-h-60 overflow-y-auto">
                {soundtracksLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                  </div>
                ) : (
                  Object.values(SOUNDTRACKS).map((soundtrack) => {
                    const isUnlocked = localUnlockedSoundtracks.includes(soundtrack.id);
                    const isSelected = currentSoundtrackId === soundtrack.id;
                    const isSelecting = selectingSoundtrack === soundtrack.id;
                    
                    return (
                      <button
                        key={soundtrack.id}
                        onClick={() => isUnlocked && handleSelectSoundtrack(soundtrack.id)}
                        disabled={!isUnlocked || isSelecting}
                        className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                          isSelected
                            ? 'border-pink-400 bg-pink-50'
                            : isUnlocked
                            ? 'border-amber-200 bg-white hover:border-pink-300 hover:bg-pink-25'
                            : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                        }`}
                        data-testid={`soundtrack-${soundtrack.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{soundtrack.emoji}</span>
                            <div>
                              <div className="font-display font-semibold text-sm text-amber-900 flex items-center gap-2">
                                {soundtrack.name}
                                {isSelected && (
                                  <Check className="w-4 h-4 text-pink-500" />
                                )}
                              </div>
                              <p className="text-xs text-amber-600">
                                {isUnlocked ? soundtrack.description : (
                                  <span className="flex items-center gap-1 text-gray-500">
                                    <LockKeyhole className="w-3 h-3" />
                                    {soundtrack.description}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          {isSelecting && (
                            <Loader2 className="w-4 h-4 animate-spin text-pink-500" />
                          )}
                          {!isUnlocked && (
                            <LockKeyhole className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
                
                {/* Tip about unlocking */}
                <p className="text-xs text-amber-600 text-center pt-2 border-t border-amber-100">
                  💡 Complete the last level of each world to unlock new soundtracks!
                </p>
              </div>
            )}
          </div>

          {/* Password Change Section */}
          <div className="p-4 bg-white/60 rounded-xl border border-amber-200">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowPasswordSection(!showPasswordSection)}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-100">
                  <Lock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <Label className="font-display font-semibold text-amber-900 cursor-pointer">
                    Change Password
                  </Label>
                  <p className="text-xs text-amber-600">Update your account password</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-amber-700"
                data-testid="toggle-password-section"
              >
                {showPasswordSection ? "Cancel" : "Change"}
              </Button>
            </div>

            {/* Password Change Form */}
            {showPasswordSection && (
              <div className="mt-4 space-y-3 pt-4 border-t border-amber-200">
                {/* Current Password */}
                <div className="space-y-1">
                  <Label className="text-sm text-amber-800">Current Password</Label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pr-10 bg-white"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-500 hover:text-amber-700"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-1">
                  <Label className="text-sm text-amber-800">New Password</Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="pr-10 bg-white"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-500 hover:text-amber-700"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-1">
                  <Label className="text-sm text-amber-800">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat new password"
                      className={`pr-10 bg-white ${confirmPassword && newPassword !== confirmPassword ? 'border-red-500' : confirmPassword && newPassword === confirmPassword ? 'border-green-500' : ''}`}
                      data-testid="confirm-password-input"
                    />
                    {confirmPassword && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2">
                        {newPassword === confirmPassword ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <X className="w-4 h-4 text-red-500" />
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleChangePassword}
                  disabled={isChangingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white font-display"
                  data-testid="change-password-btn"
                >
                  {isChangingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="p-3 bg-amber-100/50 rounded-lg border border-amber-200">
            <p className="text-xs text-amber-700 text-center">
              💡 <span className="font-semibold">Tip:</span> Your settings are saved automatically
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button 
            onClick={handleClose}
            className="font-display bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="settings-close-btn"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Settings button component for easy use
export function SettingsButton({ onClick, className = "" }) {
  return (
    <Button
      onClick={onClick}
      size="sm"
      variant="ghost"
      className={`text-white hover:bg-white/20 ${className}`}
      data-testid="settings-btn"
    >
      <Settings className="w-4 h-4" />
    </Button>
  );
}
